import { Button } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Sprite } from 'pixi.js';

import { Shared } from '../../App';

export const defaultAnimatedOpts = {
    accessible: false,
    accessibleTitle: 'Pause',
    tabIndex: 0,

    hoverScale: 1.1,
    clickScale: 1.1,
    standardScale: 1,
    startHidden: false,
    clickAudio: 'btn-click',
};

export type AnimatedButtonOpts = typeof defaultAnimatedOpts;

export class AnimatedButton extends Button
{
    protected hoverScale: number;
    protected clickScale: number;
    protected standardScale: number;

    private _clickAudio: string;

    constructor(view: Sprite, opts: Partial<AnimatedButtonOpts> = {})
    {
        const o = { ...defaultAnimatedOpts, ...opts };

        super(view, o);

        this.hoverScale = o.hoverScale;
        this.clickScale = o.clickScale;
        this.standardScale = o.standardScale;

        this._clickAudio = o.clickAudio;

        if (o.startHidden) this.shown = false;

        this.view.zIndex = 0;

        this.onOut.connect(() =>
        {
            this.view.zIndex = 0;
        });
    }

    public down(): void
    {
        if (!this.enabled) return;
        gsap.killTweensOf(this.view.scale);

        gsap.to(this.view.scale, {
            duration: 0.14,
            x: this.clickScale,
            y: this.clickScale,
            ease: 'back.out',
        });

        Shared.game.soundSystem.playSfx(this._clickAudio);

        this.view.zIndex = 1;
    }

    public up(): void
    {
        gsap.killTweensOf(this.view.scale);

        gsap.to(this.view.scale, {
            duration: 0.3,
            x: this.standardScale,
            y: this.standardScale,
            ease: 'back.inOut(2)',
        });
    }

    public hover(): void
    {
        if (!this.enabled) return;
        gsap.killTweensOf(this.view.scale);

        gsap.to(this.view.scale, {
            duration: 0.8,
            x: this.hoverScale,
            y: this.hoverScale,
            ease: 'elastic.out(1.6, 0.3)',
        });

        Shared.game.soundSystem.playSfx('btn-hover');

        this.view.zIndex = 1;
    }

    public async show(animated = true, delay?: number): Promise<void>
    {
        if (this.shown) return;
        this.shown = true;
        this.enabled = false;
        this.view.visible = true;
        gsap.killTweensOf(this.view.scale);

        const duration = animated ? 0.2 : 0;

        this.view.scale.set(0);
        await gsap.to(this.view.scale, { x: 1, y: 1, duration, ease: 'back.out', delay });
        this.enabled = true;
    }

    public async hide(animated = true): Promise<void>
    {
        if (!this.shown) return;
        this.enabled = false;

        gsap.killTweensOf(this.view.scale);

        if (animated)
        {
            await gsap.to(this.view.scale, { x: 0.5, y: 0.5, duration: 0.2, ease: 'back.in' });
        }
        this.shown = false;
        this.view.visible = false;
    }
    
}
